/**
 * Log Aggregation Service
 * Centralized logging service that can send logs to various aggregation services
 *
 * @author Claude
 */

export interface LogEntry {
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error' | 'fatal';
  message: string;
  category?: string;
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
  metadata?: Record<string, any>;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
  performance?: {
    loadTime?: number;
    renderTime?: number;
    networkTime?: number;
  };
}

export interface LogAggregationConfig {
  enabled: boolean;
  endpoint?: string;
  apiKey?: string;
  batchSize: number;
  flushInterval: number; // milliseconds
  maxRetries: number;
  service: 'elasticsearch' | 'datadog' | 'newrelic' | 'cloudwatch' | 'custom';
}

class LogAggregationService {
  private config: LogAggregationConfig;
  private logBuffer: LogEntry[] = [];
  private flushTimer: NodeJS.Timeout | null = null;
  private sessionId: string;

  constructor(config: LogAggregationConfig) {
    this.config = config;
    this.sessionId = this.generateSessionId();

    if (this.config.enabled) {
      this.startFlushTimer();
      this.setupErrorHandlers();
      this.setupPerformanceMonitoring();
    }
  }

  /**
   * Log a message
   */
  log(level: LogEntry['level'], message: string, metadata?: Record<string, any>) {
    if (!this.config.enabled) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      category: metadata?.category || 'application',
      userId: this.getCurrentUserId(),
      sessionId: this.sessionId,
      url: window.location.href,
      userAgent: navigator.userAgent,
      metadata,
    };

    this.addToBuffer(entry);
  }

  /**
   * Log debug message
   */
  debug(message: string, metadata?: Record<string, any>) {
    this.log('debug', message, metadata);
  }

  /**
   * Log info message
   */
  info(message: string, metadata?: Record<string, any>) {
    this.log('info', message, metadata);
  }

  /**
   * Log warning message
   */
  warn(message: string, metadata?: Record<string, any>) {
    this.log('warn', message, metadata);
  }

  /**
   * Log error message
   */
  error(message: string, error?: Error, metadata?: Record<string, any>) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'error',
      message,
      category: metadata?.category || 'error',
      userId: this.getCurrentUserId(),
      sessionId: this.sessionId,
      url: window.location.href,
      userAgent: navigator.userAgent,
      metadata,
      error: error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : undefined,
    };

    this.addToBuffer(entry);
  }

  /**
   * Log fatal error
   */
  fatal(message: string, error?: Error, metadata?: Record<string, any>) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'fatal',
      message,
      category: metadata?.category || 'fatal',
      userId: this.getCurrentUserId(),
      sessionId: this.sessionId,
      url: window.location.href,
      userAgent: navigator.userAgent,
      metadata,
      error: error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : undefined,
    };

    this.addToBuffer(entry);
    // Immediately flush fatal errors
    this.flush();
  }

  /**
   * Log performance metrics
   */
  performance(metrics: LogEntry['performance'], metadata?: Record<string, any>) {
    if (!this.config.enabled) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'info',
      message: 'Performance metrics',
      category: 'performance',
      userId: this.getCurrentUserId(),
      sessionId: this.sessionId,
      url: window.location.href,
      userAgent: navigator.userAgent,
      metadata,
      performance: metrics,
    };

    this.addToBuffer(entry);
  }

  /**
   * Log user interaction
   */
  userAction(action: string, element?: string, metadata?: Record<string, any>) {
    this.log('info', `User action: ${action}`, {
      category: 'user-interaction',
      action,
      element,
      ...metadata,
    });
  }

  /**
   * Log API call
   */
  apiCall(
    method: string,
    url: string,
    status: number,
    duration: number,
    metadata?: Record<string, any>
  ) {
    this.log('info', `API call: ${method} ${url}`, {
      category: 'api',
      method,
      url,
      status,
      duration,
      ...metadata,
    });
  }

  /**
   * Log navigation event
   */
  navigation(from: string, to: string, metadata?: Record<string, any>) {
    this.log('info', `Navigation: ${from} -> ${to}`, {
      category: 'navigation',
      from,
      to,
      ...metadata,
    });
  }

  /**
   * Add entry to buffer
   */
  private addToBuffer(entry: LogEntry) {
    this.logBuffer.push(entry);

    if (this.logBuffer.length >= this.config.batchSize) {
      this.flush();
    }
  }

  /**
   * Flush logs to aggregation service
   */
  private async flush() {
    if (this.logBuffer.length === 0) return;

    const logsToSend = [...this.logBuffer];
    this.logBuffer = [];

    try {
      await this.sendLogs(logsToSend);
    } catch (error) {
      console.error('Failed to send logs to aggregation service:', error);
      // Re-add logs to buffer for retry (up to max retries)
      this.logBuffer.unshift(...logsToSend);
    }
  }

  /**
   * Send logs to the configured aggregation service
   */
  private async sendLogs(logs: LogEntry[], retryCount = 0): Promise<void> {
    if (retryCount >= this.config.maxRetries) {
      console.error('Max retries reached for log aggregation');
      return;
    }

    try {
      switch (this.config.service) {
        case 'elasticsearch':
          await this.sendToElasticsearch(logs);
          break;
        case 'datadog':
          await this.sendToDatadog(logs);
          break;
        case 'newrelic':
          await this.sendToNewRelic(logs);
          break;
        case 'cloudwatch':
          await this.sendToCloudWatch(logs);
          break;
        case 'custom':
          await this.sendToCustomEndpoint(logs);
          break;
        default:
          throw new Error(`Unsupported log aggregation service: ${this.config.service}`);
      }
    } catch (error) {
      console.error(`Failed to send logs (attempt ${retryCount + 1}):`, error);

      // Retry with exponential backoff
      setTimeout(
        () => {
          this.sendLogs(logs, retryCount + 1);
        },
        Math.pow(2, retryCount) * 1000
      );
    }
  }

  /**
   * Send logs to Elasticsearch
   */
  private async sendToElasticsearch(logs: LogEntry[]): Promise<void> {
    if (!this.config.endpoint) throw new Error('Elasticsearch endpoint not configured');

    const body =
      logs
        .map(log => [
          JSON.stringify({ index: { _index: 'application-logs' } }),
          JSON.stringify(log),
        ])
        .flat()
        .join('\n') + '\n';

    const response = await fetch(`${this.config.endpoint}/_bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-ndjson',
        ...(this.config.apiKey && { Authorization: `ApiKey ${this.config.apiKey}` }),
      },
      body,
    });

    if (!response.ok) {
      throw new Error(`Elasticsearch error: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Send logs to Datadog
   */
  private async sendToDatadog(logs: LogEntry[]): Promise<void> {
    if (!this.config.apiKey) throw new Error('Datadog API key not configured');

    const response = await fetch(
      'https://http-intake.logs.datadoghq.com/v1/input/' + this.config.apiKey,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logs),
      }
    );

    if (!response.ok) {
      throw new Error(`Datadog error: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Send logs to New Relic
   */
  private async sendToNewRelic(logs: LogEntry[]): Promise<void> {
    if (!this.config.apiKey) throw new Error('New Relic API key not configured');

    const response = await fetch('https://log-api.newrelic.com/log/v1', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-License-Key': this.config.apiKey,
      },
      body: JSON.stringify({ logs }),
    });

    if (!response.ok) {
      throw new Error(`New Relic error: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Send logs to AWS CloudWatch
   */
  private async sendToCloudWatch(logs: LogEntry[]): Promise<void> {
    // This would require AWS SDK setup
    // For now, send to custom endpoint that handles CloudWatch integration
    await this.sendToCustomEndpoint(logs);
  }

  /**
   * Send logs to custom endpoint
   */
  private async sendToCustomEndpoint(logs: LogEntry[]): Promise<void> {
    if (!this.config.endpoint) throw new Error('Custom endpoint not configured');

    const response = await fetch(this.config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.config.apiKey && { Authorization: `Bearer ${this.config.apiKey}` }),
      },
      body: JSON.stringify({ logs }),
    });

    if (!response.ok) {
      throw new Error(`Custom endpoint error: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Start flush timer
   */
  private startFlushTimer() {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.config.flushInterval);
  }

  /**
   * Setup global error handlers
   */
  private setupErrorHandlers() {
    // Unhandled errors
    window.addEventListener('error', event => {
      this.error('Unhandled error', event.error, {
        category: 'global-error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', event => {
      this.error('Unhandled promise rejection', undefined, {
        category: 'promise-rejection',
        reason: event.reason,
      });
    });
  }

  /**
   * Setup performance monitoring
   */
  private setupPerformanceMonitoring() {
    // Page load performance
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = performance.getEntriesByType(
          'navigation'
        )[0] as PerformanceNavigationTiming;
        if (perfData) {
          this.performance(
            {
              loadTime: perfData.loadEventEnd - perfData.loadEventStart,
              networkTime: perfData.responseEnd - perfData.requestStart,
              renderTime: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
            },
            {
              category: 'page-performance',
              url: window.location.href,
            }
          );
        }
      }, 1000);
    });

    // Resource performance monitoring
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'resource') {
          const resourceEntry = entry as PerformanceResourceTiming;
          this.performance(
            {
              loadTime: resourceEntry.duration,
            },
            {
              category: 'resource-performance',
              resourceType: resourceEntry.initiatorType,
              resourceName: resourceEntry.name,
            }
          );
        }
      }
    });

    observer.observe({ entryTypes: ['resource'] });
  }

  /**
   * Get current user ID from auth context
   */
  private getCurrentUserId(): string | undefined {
    // This would integrate with your auth system
    // For now, try to get from localStorage or session
    try {
      const authData = localStorage.getItem('auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.user?.id || parsed.user?.email;
      }
    } catch {
      // Ignore errors
    }
    return undefined;
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Cleanup resources
   */
  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // Flush remaining logs
    this.flush();
  }
}

// Default configuration
const defaultConfig: LogAggregationConfig = {
  enabled: process.env.NODE_ENV === 'production' && !!process.env.REACT_APP_LOG_ENDPOINT,
  batchSize: 10,
  flushInterval: 30000, // 30 seconds
  maxRetries: 3,
  service: 'custom',
  endpoint: process.env.REACT_APP_LOG_ENDPOINT,
  apiKey: process.env.REACT_APP_LOG_API_KEY,
};

// Create singleton instance
export const logService = new LogAggregationService(defaultConfig);

// Export for custom configurations
export default LogAggregationService;
